import React from "react";
import type { TypedWrappedFieldProps } from "redux-form";
import dayjs from "dayjs";

import type {
  DatePickerProps,
  DatePopoverProps,
  DoubleDatePopoverPickerProps,
} from "@braintrust/braintrust-ui-components";
import {
  DATE_FORMAT,
  DatePicker,
  DatePopover,
  DoubleDatePopoverPicker,
} from "@braintrust/braintrust-ui-components";
import { DateFormats } from "@js/utils/date/types";

// format prop doesn't work when using through the redux-form Field
type DateFieldWitDateFormatProps = DatePickerProps & {
  dateFormat?: DatePickerProps["format"];
};

export const ReduxFormDatePickerFieldFactory =
  (DatePickerComponent: React.FC<React.PropsWithChildren<DatePickerProps>>) =>
  ({
    id,
    input,
    meta,
    dateFormat,
    ...rest
  }: DistributiveOmit<DateFieldWitDateFormatProps, "value" | "onChange"> &
    TypedWrappedFieldProps<string> &
    Pick<DatePopoverProps, "Anchor" | "anchorProps">) => {
    const error =
      (meta.submitFailed || meta.touched) && meta.error
        ? meta.error
        : undefined;

    const defaultFormat = DateFormats["1970-12-31"];
    const pickerDateFormat = dateFormat ?? defaultFormat;
    return (
      <DatePickerComponent
        id={id}
        label="Date"
        error={error}
        helperText={error}
        value={input.value ? dayjs.utc(input.value) : null}
        onChange={(value) => {
          input.onChange(value ? value.format(defaultFormat) : null);
        }}
        onBlur={() => {
          // trigger touched
          input.onBlur(input.value || null);
        }}
        format={pickerDateFormat}
        {...rest}
      />
    );
  };

export type DoubleDatePopoverPickerFieldProps = Pick<
  DoubleDatePopoverPickerProps,
  "id" | "hideError" | "label" | "labelIcon" | "disablePast"
> & {
  startDateField: TypedWrappedFieldProps<string>;
  endDateField: TypedWrappedFieldProps<string>;
};

export const DoubleDatePopoverPickerField = ({
  id,
  hideError = false,
  startDateField: start,
  endDateField: end,
  ...rest
}: DoubleDatePopoverPickerFieldProps) => {
  const startError =
    (start.meta.submitFailed || start.meta.touched) && start.meta.error
      ? (start.meta.error as string)
      : undefined;
  const endError =
    (end.meta.submitFailed || end.meta.touched) && end.meta.error
      ? (end.meta.error as string)
      : undefined;

  const startDate = start.input.value;
  const endDate = end.input.value;

  return (
    <DoubleDatePopoverPicker
      id={id}
      error={startError || endError}
      startDate={startDate ? dayjs.utc(startDate) : null}
      onStartDateChange={(value) => {
        start.input.onChange(value ? value.format("YYYY-MM-DD") : null);
      }}
      onStartBlur={() => {
        // we don't want to pass event as argument because
        // value will be set from null to invalid keyboard input value for some reason
        start.input.onBlur(undefined);
      }}
      endDate={endDate ? dayjs.utc(endDate) : null}
      onEndDateChange={(value) => {
        end.input.onChange(value ? value.format("YYYY-MM-DD") : null);
      }}
      onEndBlur={() => {
        end.input.onBlur(undefined);
      }}
      {...rest}
      hideError={hideError}
    />
  );
};

export const DateField = ReduxFormDatePickerFieldFactory(DatePicker);
export const DatePopoverField = ReduxFormDatePickerFieldFactory(
  DatePopover as React.FC<React.PropsWithChildren<DatePickerProps>>,
);

export { DATE_FORMAT };
